/**
 * Auto-select the first PURCHASABLE variant on the product page.
 *
 * Shopify's `selected_or_first_available_variant` picks the first IN-STOCK
 * variant, which is not the same thing as the first sellable one: a variant
 * with a blank SKU is in stock but can't be delivered on any date (see
 * snippets/product-unavailable.liquid), so its row renders disabled by
 * variant-main-picker.liquid. Landing on it means the customer opens the PDP
 * to a disabled "Currently unavailable" button on a product that is perfectly
 * buyable in another size.
 *
 * Rather than overriding the selection in Liquid — which would desync the
 * checked radio from the price, media and add-to-cart variant id, all of which
 * read from the section's own selected variant — this dispatches a real change
 * on the first purchasable input. Horizon's variant picker then runs its normal
 * flow: update the URL, re-render the section, and everything stays consistent.
 *
 * Re-entry is safe. After the change the URL carries ?variant=<good id>, so the
 * re-rendered section has a purchasable variant checked and this exits early.
 * If every variant is unavailable there is nothing better to pick, so it also
 * exits and the disabled state stands.
 */

const selectFirstPurchasable = (picker) => {
  const inputs = Array.from(picker.querySelectorAll('.step-option__input'));
  if (!inputs.length) return;

  const checked = inputs.find((input) => input.checked);
  // Only intervene when the current selection is the unsellable kind. A
  // sold-out selection is a legitimate merchandising state — leave it alone.
  if (!checked || checked.dataset.noSku !== 'true') return;

  const replacement = inputs.find(
    (input) => input.dataset.noSku !== 'true' && input.dataset.optionAvailable !== 'false'
  );
  if (!replacement || replacement === checked) return;

  replacement.checked = true;
  replacement.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * Strip the unavailable styling from any button that is no longer disabled.
 *
 * Horizon morphs the add-to-cart button in place when the variant changes, and
 * the morph can keep the old `--unavailable` class while updating the label and
 * clearing `disabled`. That leaves a perfectly working "Add to cart" wearing the
 * warning styling. The CSS is already scoped to `:disabled` so this is belt and
 * braces, but it keeps the DOM honest — the class should never sit on a button
 * the customer can actually press.
 */
const clearStaleUnavailableState = () => {
  document
    .querySelectorAll('.add-to-cart-button--unavailable, .product-card-custom__atc--unavailable')
    .forEach((button) => {
      if (button.disabled === true) return;
      if (button.getAttribute('aria-disabled') === 'true') return;
      button.classList.remove('add-to-cart-button--unavailable', 'product-card-custom__atc--unavailable');
    });
};

const run = () => {
  document.querySelectorAll('variant-picker').forEach(selectFirstPurchasable);
  clearStaleUnavailableState();
};

// The section re-renders on every variant change, so re-check after each one.
document.addEventListener('variant:update', clearStaleUnavailableState);
document.addEventListener('change', (event) => {
  if (event.target instanceof Element && event.target.closest('variant-picker')) {
    setTimeout(clearStaleUnavailableState, 300);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run, { once: true });
} else {
  run();
}
